import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export type Size = {
    width: number;
    height: number;
};

/**
 * Hook that will always calculate the correct size of an HTMLElement (width + height). The hook returns both the size
 * and an element setter in an array structure similar to the useState hook. In most cases you would want to call the
 * element setter inside the ref callback of a component like ref={(element) => setElement(element)}.
 *
 * The dimensions will automatically update each time the component resizes.
 *
 * @param externalElementState Optional useState hook reference that can be passed in, when the HTMLElement also needs
 * to be referenced outside of this hook.
 */
export function useComponentSize<T extends HTMLElement | null>(
    externalElementState?: [T | null, Dispatch<SetStateAction<T | null>>]
): [Size, (element: T) => void] {
    const internalElementState = useState<T | null>(null);
    const [element, setElement] = externalElementState ?? internalElementState;
    const [size, setSize] = useState<Size>({ width: 0, height: 0 });
    useEffect(() => {
        if (!element) {
            // No element to calculate width for
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            const elementRect = element.getBoundingClientRect();
            setSize({ width: elementRect.width || 0, height: elementRect.height || 0 });
        });

        resizeObserver.observe(element);
        return () => {
            resizeObserver.disconnect();
        };
    }, [element]);
    return [size, setElement];
}
